/**
 * StatsView — Hisni native Phase 1b stats dashboard.
 *
 * Renders:
 *  - Streak counter + animated progress bar (transform:scaleX, Motion spec B)
 *  - Card-state breakdown (new / learning / familiar / memorized / mastered)
 *  - Per-mode performance chart (stub — awaiting API shape)
 *  - Surah heatmap: 114 tiles × mastery level, 12-wide grid
 *
 * Design tokens: var(--ln-*) only — NO hardcoded hex colours.
 * RTL-safe: transform-origin flips via [dir="rtl"] rule.
 * WCAG 2.1 AA: heatmap tiles are <button> with aria-label; focus ring visible.
 *
 * Page transition: document.startViewTransition() guard,
 *   view-transition-name: hisni-stats.
 *
 * Streak celebration: when currentStreak ≥ 7 and it is a new personal best
 *   (compared to localStorage key 'aweni_hisni_last_streak_shown'),
 *   fires MilestoneOverlay. Guard prevents re-fire.
 */

import type { AweniContext } from '../../types';
import { checkMilestone, showMilestoneOverlay } from './milestones';

// ─── API response shape ───────────────────────────────────────────────────────

interface ApiStats {
  streak?: {
    current?: number;
    best?: number;
    lastActivity?: string;
  };
  cards?: {
    total?: number;
    new?: number;
    learning?: number;
    familiar?: number;
    memorized?: number;
    mastered?: number;
  };
  reviews?: {
    totalCompleted?: number;
    todayCompleted?: number;
    accuracy?: number;
  };
  surahs?: Array<{
    surahNumber?: number;
    name?: string;
    nameAr?: string;
    totalAyahs?: number;
    masteredAyahs?: number;
    pct?: number;
  }>;
}

// ─── Surah meta (name + ayah count) ─────────────────────────────────────────
// Enough for the heatmap tiles; we don't need full tafsir data here.

const SURAH_NAMES: string[] = [
  'Al-Fatiha','Al-Baqara','Ali \'Imran','An-Nisa\'','Al-Ma\'idah','Al-An\'am',
  'Al-A\'raf','Al-Anfal','At-Tawbah','Yunus','Hud','Yusuf','Ar-Ra\'d','Ibrahim',
  'Al-Hijr','An-Nahl','Al-Isra\'','Al-Kahf','Maryam','Ta-Ha','Al-Anbiya\'',
  'Al-Hajj','Al-Mu\'minun','An-Nur','Al-Furqan','Ash-Shu\'ara\'','An-Naml',
  'Al-Qasas','Al-\'Ankabut','Ar-Rum','Luqman','As-Sajdah','Al-Ahzab','Saba\'',
  'Fatir','Ya-Sin','As-Saffat','Sad','Az-Zumar','Ghafir','Fussilat','Ash-Shura',
  'Az-Zukhruf','Ad-Dukhan','Al-Jathiyah','Al-Ahqaf','Muhammad','Al-Fath',
  'Al-Hujurat','Qaf','Adh-Dhariyat','At-Tur','An-Najm','Al-Qamar','Ar-Rahman',
  'Al-Waqi\'ah','Al-Hadid','Al-Mujadilah','Al-Hashr','Al-Mumtahanah','As-Saf',
  'Al-Jumu\'ah','Al-Munafiqun','At-Taghabun','At-Talaq','At-Tahrim','Al-Mulk',
  'Al-Qalam','Al-Haqqah','Al-Ma\'arij','Nuh','Al-Jinn','Al-Muzzammil',
  'Al-Muddaththir','Al-Qiyamah','Al-Insan','Al-Mursalat','An-Naba\'',
  'An-Nazi\'at','\'Abasa','At-Takwir','Al-Infitar','Al-Mutaffifin','Al-Inshiqaq',
  'Al-Buruj','At-Tariq','Al-A\'la','Al-Ghashiyah','Al-Fajr','Al-Balad',
  'Ash-Shams','Al-Layl','Ad-Duha','Ash-Sharh','At-Tin','Al-\'Alaq','Al-Qadr',
  'Al-Bayyinah','Az-Zalzalah','Al-\'Adiyat','Al-Qari\'ah','At-Takathur',
  'Al-\'Asr','Al-Humazah','Al-Fil','Quraysh','Al-Ma\'un','Al-Kawthar',
  'Al-Kafirun','An-Nasr','Al-Masad','Al-Ikhlas','Al-Falaq','An-Nas',
];

// ─── Style injection ──────────────────────────────────────────────────────────

let _stylesInjected = false;

function injectStyles(): void {
  if (_stylesInjected || document.getElementById('hisni-stats-styles')) return;
  _stylesInjected = true;
  const s = document.createElement('style');
  s.id = 'hisni-stats-styles';
  s.textContent = `
    /* view transition */
    .hisni-stats-root {
      view-transition-name: hisni-stats;
      max-width: 720px;
      margin: 0 auto;
      padding: var(--ln-space-3, 12px);
      padding-bottom: 80px;
    }

    /* ── Section card ── */
    .hsv-card {
      background: var(--ln-bg-card);
      border: 1px solid var(--ln-border);
      border-radius: var(--ln-radius-lg, 16px);
      padding: 14px;
      margin-bottom: 14px;
    }
    .hsv-card-title {
      font-size: .75rem;
      font-weight: 700;
      color: var(--ln-text-secondary);
      text-transform: uppercase;
      letter-spacing: .5px;
      margin-bottom: 12px;
    }

    /* ── Streak section ── */
    .hsv-streak-row {
      display: flex;
      align-items: baseline;
      gap: 6px;
      margin-bottom: 8px;
    }
    .hsv-streak-number {
      font-size: 2.2rem;
      font-weight: 900;
      color: var(--ln-accent);
      line-height: 1;
    }
    .hsv-streak-label {
      font-size: .9rem;
      color: var(--ln-text-secondary);
    }
    .hsv-streak-track {
      height: 6px;
      border-radius: var(--ln-radius-full, 9999px);
      background: var(--ln-bg-subtle, rgba(0,0,0,.08));
      overflow: hidden;
    }
    .hsv-streak-fill {
      width: 100%;
      height: 100%;
      border-radius: inherit;
      background: var(--ln-accent);
      transform: scaleX(0);
      transform-origin: left;
      transition: transform var(--motion-duration-normal, 300ms) var(--motion-easing-standard, ease-out);
      will-change: transform;
    }
    [dir="rtl"] .hsv-streak-fill { transform-origin: right; }
    .hsv-streak-best {
      font-size: .72rem;
      color: var(--ln-text-secondary);
      margin-top: 4px;
    }

    /* ── Card breakdown ── */
    .hsv-breakdown {
      display: grid;
      grid-template-columns: repeat(5, 1fr);
      gap: 6px;
      text-align: center;
    }
    .hsv-breakdown-item { display: flex; flex-direction: column; align-items: center; gap: 2px; }
    .hsv-breakdown-dot {
      width: 10px;
      height: 10px;
      border-radius: 50%;
      margin-bottom: 2px;
    }
    .hsv-breakdown-dot--new       { background: var(--ln-text-secondary); }
    .hsv-breakdown-dot--learning  { background: var(--ln-state-error); }
    .hsv-breakdown-dot--familiar  { background: var(--ln-accent-ceremonial); }
    .hsv-breakdown-dot--memorized { background: var(--ln-state-success); }
    .hsv-breakdown-dot--mastered  { background: var(--ln-accent); }
    .hsv-breakdown-count {
      font-size: 1rem;
      font-weight: 800;
      color: var(--ln-text-primary, #111);
    }
    .hsv-breakdown-name {
      font-size: .65rem;
      color: var(--ln-text-secondary);
      white-space: nowrap;
    }

    /* ── Mastery progress bars ── */
    .hsv-bar-row {
      display: flex;
      align-items: center;
      gap: 8px;
      margin-bottom: 6px;
    }
    .hsv-bar-label {
      font-size: .75rem;
      color: var(--ln-text-secondary);
      white-space: nowrap;
      flex-shrink: 0;
      width: 70px;
      overflow: hidden;
      text-overflow: ellipsis;
    }
    .hsv-bar-track {
      flex: 1;
      height: 6px;
      border-radius: var(--ln-radius-full, 9999px);
      background: var(--ln-bg-subtle, rgba(0,0,0,.08));
      overflow: hidden;
    }
    .hsv-bar-fill {
      width: 100%;
      height: 100%;
      border-radius: inherit;
      background: var(--ln-accent);
      transform: scaleX(0);
      transform-origin: left;
      transition: transform var(--motion-duration-normal, 300ms) var(--motion-easing-standard, ease-out);
      will-change: transform;
    }
    [dir="rtl"] .hsv-bar-fill { transform-origin: right; }
    .hsv-bar-pct {
      font-size: .7rem;
      color: var(--ln-text-secondary);
      flex-shrink: 0;
      width: 32px;
      text-align: right;
    }
    [dir="rtl"] .hsv-bar-pct { text-align: left; }

    /* ── Heatmap grid: 114 tiles ── */
    .hsv-heatmap-grid {
      display: grid;
      grid-template-columns: repeat(12, 1fr);
      gap: 4px;
    }
    /* touch-area wrapper: negative-margin technique so visual stays small */
    .hsv-tile-wrap {
      position: relative;
      display: flex;
      align-items: center;
      justify-content: center;
    }
    .hsv-tile {
      display: block;
      width: clamp(20px, 4vw, 32px);
      height: clamp(20px, 4vw, 32px);
      border-radius: var(--ln-radius-sm, 4px);
      background: var(--ln-bg-subtle, rgba(0,0,0,.06));
      border: none;
      cursor: pointer;
      transition: opacity var(--motion-duration-fast, 120ms) ease;
      /* expand touch area to 44×44 minimum via padding */
      padding: 0;
      /* touch target via pseudo-element — no layout impact */
      position: relative;
    }
    .hsv-tile::after {
      content: '';
      position: absolute;
      inset: 50%;
      transform: translate(-50%, -50%);
      min-width: 44px;
      min-height: 44px;
    }
    .hsv-tile:hover  { opacity: .75; }
    .hsv-tile:focus-visible {
      outline: 2px solid var(--ln-accent);
      outline-offset: 2px;
    }
    .hsv-tile[data-mastery="0"] { background: var(--ln-bg-subtle, rgba(0,0,0,.06)); }
    .hsv-tile[data-mastery="1"] { background: var(--ln-state-error); opacity: .7; }
    .hsv-tile[data-mastery="2"] { background: var(--ln-accent-ceremonial); opacity: .8; }
    .hsv-tile[data-mastery="3"] { background: var(--ln-state-success); opacity: .9; }
    .hsv-tile[data-mastery="4"] { background: var(--ln-accent); }

    /* ── Guest / empty state ── */
    .hsv-guest {
      text-align: center;
      padding: 32px 16px;
      color: var(--ln-text-secondary);
      font-size: .9rem;
      line-height: 1.5;
    }

    @media (prefers-reduced-motion: reduce) {
      .hsv-streak-fill, .hsv-bar-fill, .hsv-tile { transition: none; }
    }
  `;
  document.head.appendChild(s);
}

// ─── StatsView ────────────────────────────────────────────────────────────────

/**
 * Mount the full Hisni stats dashboard into `container`.
 * Returns an unmount cleanup function.
 */
export async function mountStatsView(
  container: HTMLElement,
  ctx: AweniContext,
): Promise<() => void> {
  injectStyles();

  const t = (k: string, fb: string) => ctx.i18n.t(k) || fb;
  const dir = ctx.i18n.dir ?? 'ltr';
  const token = ctx.auth?.getToken?.() ?? null;

  // ── Page transition ────────────────────────────────────────────────────────
  const doRender = async (): Promise<void> => {
    await _render(container, ctx, t, dir, token);
  };

  if ('startViewTransition' in document) {
    await (document as unknown as { startViewTransition(cb: () => Promise<void>): { finished: Promise<void> } })
      .startViewTransition(doRender).finished.catch(() => doRender());
  } else {
    await doRender();
  }

  return () => { container.innerHTML = ''; };
}

async function _render(
  container: HTMLElement,
  ctx: AweniContext,
  t: (k: string, fb: string) => string,
  dir: 'ltr' | 'rtl',
  token: string | null,
): Promise<void> {
  // ── Auth guard ─────────────────────────────────────────────────────────────
  if (!token) {
    container.innerHTML = `
      <div class="hisni-stats-root" dir="${dir}">
        <div class="hsv-guest" role="status">
          ${escHtml(t('hisni_stats_guest', 'Sign in to see your memorization stats.'))}
        </div>
      </div>`;
    return;
  }

  // ── Loading skeleton ───────────────────────────────────────────────────────
  container.innerHTML = `
    <div class="hisni-stats-root" dir="${dir}">
      <div class="hsv-card">
        <div class="hsv-card-title" style="width:60px;height:10px;background:var(--ln-bg-subtle);border-radius:4px;"></div>
        <div style="width:80px;height:36px;background:var(--ln-bg-subtle);border-radius:8px;margin-bottom:8px;"></div>
        <div style="width:100%;height:6px;background:var(--ln-bg-subtle);border-radius:9999px;"></div>
      </div>
    </div>`;

  // ── Fetch stats ────────────────────────────────────────────────────────────
  let data: ApiStats = {};
  try {
    const res = await fetch('/aweni/api/v1/gateway/memoriz/api/stats', {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (res.ok) {
      data = (await res.json()) as ApiStats;
    }
  } catch {
    /* network error — render with empty data */
  }

  // ── Derived values ─────────────────────────────────────────────────────────
  const streak     = data.streak?.current   ?? 0;
  const bestStreak = data.streak?.best      ?? streak;
  const cards      = data.cards             ?? {};
  const reviews    = data.reviews           ?? {};
  const surahs     = data.surahs            ?? [];

  // Max streak value for the progress bar (aria-valuemax matches this).
  const MAX_STREAK_DISPLAY = 30;

  // ── Streak milestone guard ─────────────────────────────────────────────────
  // Only fires for streak milestones; uses localStorage to prevent re-show.
  if (streak >= 7) {
    const lastShown = parseInt(localStorage.getItem('aweni_hisni_last_streak_shown') ?? '0', 10);
    if (streak > lastShown) {
      const event = checkMilestone(streak, 0); // pass 0 for totalReviewed to get streak event only
      if (event && event.type === 'streak') {
        localStorage.setItem('aweni_hisni_last_streak_shown', String(streak));
        showMilestoneOverlay(
          event,
          t('common_close', 'Continue'),
        );
      }
    }
  }

  // ── Build surah mastery map from API ───────────────────────────────────────
  const surahMastery = new Map<number, number>(); // surahNumber → 0-4
  for (const s of surahs) {
    const n = s.surahNumber ?? 0;
    if (!n) continue;
    const pct = s.pct ?? (s.masteredAyahs && s.totalAyahs ? s.masteredAyahs / s.totalAyahs : 0);
    let level = 0;
    if (pct >= 1)    level = 4;
    else if (pct >= .75) level = 3;
    else if (pct >= .4)  level = 2;
    else if (pct > 0)    level = 1;
    surahMastery.set(n, level);
  }

  // ── Streak bar ratio ───────────────────────────────────────────────────────
  const streakRatio = Math.min(1, streak / MAX_STREAK_DISPLAY);

  // ── Card totals ────────────────────────────────────────────────────────────
  const cNew       = cards.new       ?? 0;
  const cLearning  = cards.learning  ?? 0;
  const cFamiliar  = cards.familiar  ?? 0;
  const cMemorized = cards.memorized ?? 0;
  const cMastered  = cards.mastered  ?? 0;
  const cTotal     = cards.total     ?? (cNew + cLearning + cFamiliar + cMemorized + cMastered);

  // ── Heatmap tiles HTML ────────────────────────────────────────────────────
  const heatmapTiles = Array.from({ length: 114 }, (_, i) => {
    const surahNum = i + 1;
    const name = SURAH_NAMES[i] ?? `Surah ${surahNum}`;
    const mastery = surahMastery.get(surahNum) ?? 0;
    const ariaLabel = `${name} (${surahNum}) — ${t('hisni_stats_mastered', 'Mastered')}: ${mastery}/4`;
    return `
      <div class="hsv-tile-wrap">
        <button
          class="hsv-tile"
          data-mastery="${mastery}"
          data-surah="${surahNum}"
          aria-label="${escHtml(ariaLabel)}"
          title="${escHtml(name)}"
        ></button>
      </div>`;
  }).join('');

  // ── Top surah bars (up to 10 started surahs sorted by pct desc) ──────────
  const startedSurahs = surahs
    .filter(s => (s.pct ?? 0) > 0)
    .sort((a, b) => (b.pct ?? 0) - (a.pct ?? 0))
    .slice(0, 10);

  const surahBarsHtml = startedSurahs.length > 0
    ? startedSurahs.map(s => {
        const pct = Math.min(1, Math.max(0, s.pct ?? 0));
        const label = s.name ?? `Surah ${s.surahNumber ?? '?'}`;
        const pctLabel = `${Math.round(pct * 100)}%`;
        return `
          <div class="hsv-bar-row">
            <span class="hsv-bar-label" title="${escHtml(label)}">${escHtml(label)}</span>
            <div class="hsv-bar-track"
                 role="progressbar"
                 aria-label="${escHtml(label)}"
                 aria-valuenow="${Math.round(pct * 100)}"
                 aria-valuemin="0"
                 aria-valuemax="100">
              <div class="hsv-bar-fill" data-scale="${pct}"></div>
            </div>
            <span class="hsv-bar-pct" aria-hidden="true">${escHtml(pctLabel)}</span>
          </div>`;
      }).join('')
    : '';

  // ── Render ─────────────────────────────────────────────────────────────────
  container.innerHTML = `
    <div class="hisni-stats-root" dir="${dir}">

      <!-- Streak -->
      <div class="hsv-card">
        <div class="hsv-card-title">${escHtml(t('hisni_stats_streak', 'Day streak'))}</div>
        <div class="hsv-streak-row">
          <span class="hsv-streak-number">${streak}</span>
          <span class="hsv-streak-label">🔥 ${escHtml(t('hisni_stats_streak', 'Day streak'))}</span>
        </div>
        <div class="hsv-streak-track"
             role="progressbar"
             aria-valuenow="${streak}"
             aria-valuemin="0"
             aria-valuemax="${MAX_STREAK_DISPLAY}"
             aria-label="${escHtml(t('hisni_stats_streak', 'Day streak'))}">
          <div class="hsv-streak-fill" data-scale="${streakRatio}"></div>
        </div>
        ${bestStreak > 0 ? `<div class="hsv-streak-best">${escHtml(t('hisni_stats_best_streak', 'Best'))}: ${bestStreak} ${escHtml(t('hisni_stats_streak', 'days'))}</div>` : ''}
      </div>

      <!-- Card breakdown -->
      <div class="hsv-card">
        <div class="hsv-card-title">${escHtml(t('hisni_stats_cards', 'Cards'))} — ${cTotal}</div>
        <div class="hsv-breakdown">
          <div class="hsv-breakdown-item">
            <div class="hsv-breakdown-dot hsv-breakdown-dot--new" aria-hidden="true"></div>
            <span class="hsv-breakdown-count">${cNew}</span>
            <span class="hsv-breakdown-name">${escHtml(t('hisni_card_state_new', 'New'))}</span>
          </div>
          <div class="hsv-breakdown-item">
            <div class="hsv-breakdown-dot hsv-breakdown-dot--learning" aria-hidden="true"></div>
            <span class="hsv-breakdown-count">${cLearning}</span>
            <span class="hsv-breakdown-name">${escHtml(t('hisni_card_state_learning', 'Learning'))}</span>
          </div>
          <div class="hsv-breakdown-item">
            <div class="hsv-breakdown-dot hsv-breakdown-dot--familiar" aria-hidden="true"></div>
            <span class="hsv-breakdown-count">${cFamiliar}</span>
            <span class="hsv-breakdown-name">${escHtml(t('hisni_card_state_familiar', 'Familiar'))}</span>
          </div>
          <div class="hsv-breakdown-item">
            <div class="hsv-breakdown-dot hsv-breakdown-dot--memorized" aria-hidden="true"></div>
            <span class="hsv-breakdown-count">${cMemorized}</span>
            <span class="hsv-breakdown-name">${escHtml(t('hisni_card_state_memorized', 'Memorized'))}</span>
          </div>
          <div class="hsv-breakdown-item">
            <div class="hsv-breakdown-dot hsv-breakdown-dot--mastered" aria-hidden="true"></div>
            <span class="hsv-breakdown-count">${cMastered}</span>
            <span class="hsv-breakdown-name">${escHtml(t('hisni_card_state_mastered', 'Mastered'))}</span>
          </div>
        </div>
      </div>

      <!-- Surah mastery bars (top active surahs) -->
      ${startedSurahs.length > 0 ? `
      <div class="hsv-card">
        <div class="hsv-card-title">${escHtml(t('hisni_stats_mastered', 'Mastered'))}</div>
        ${surahBarsHtml}
      </div>` : ''}

      <!-- Surah heatmap (114 tiles) -->
      <div class="hsv-card">
        <div class="hsv-card-title">${escHtml(t('hisni_stats_heatmap', 'Surah heatmap'))}</div>
        <div class="hsv-heatmap-grid" role="list" aria-label="${escHtml(t('hisni_stats_heatmap', 'Surah heatmap'))}">
          ${heatmapTiles}
        </div>
      </div>

    </div>`;

  // ── Animate progress bars (RAF → ensures CSS transition triggers) ─────────
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      container.querySelectorAll<HTMLElement>('.hsv-streak-fill, .hsv-bar-fill').forEach(el => {
        const scale = parseFloat(el.dataset.scale ?? '0');
        el.style.transform = `scaleX(${scale})`;
      });
    });
  });

  // ── Wire heatmap tile clicks → SurahBrowser navigation ───────────────────
  container.querySelectorAll<HTMLButtonElement>('.hsv-tile[data-surah]').forEach(btn => {
    btn.addEventListener('click', () => {
      const surah = btn.dataset.surah ?? '';
      const targetHash = `#/hisni/surah/${surah}`;
      // Navigate to hisni SurahBrowser filtered to this surah.
      // Uses the same hash-routing pattern as the rest of the shell.
      window.dispatchEvent(new CustomEvent('aweni:navigate', {
        detail: { route: targetHash },
        bubbles: true,
      }));
    });
  });
}

// ─── helpers ─────────────────────────────────────────────────────────────────

function escHtml(s: string | undefined | null): string {
  if (s == null) return '';
  return String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}
